/**
 * BrainyTots Assessment - Swipeable Card UI
 * Mobile-first card-based interface with gesture navigation
 */

class BrainyTotsAssessment {
  constructor() {
    this.api = new BrainyTotsAssessmentAPI();
    this.modal = null;
    
    // Question history and navigation
    this.questionHistory = [];
    this.currentQuestionIndex = -1;
    this.maxQuestions = 14;
    this.minQuestions = 10;
    
    // Session state
    this.sessionStarted = false;
    this.assessmentComplete = false;
    
    // Touch gesture state
    this.touchStartX = 0;
    this.touchStartY = 0;
    this.touchEndX = 0;
    this.touchEndY = 0;
    this.isSwiping = false;
    
    // Answer history drawer state
    this.drawerOpen = false;
    
    this.init();
  }

  init() {
    this.createModal();
    this.bindEvents();
  }

  /**
   * Create the swipeable card modal HTML
   */
  createModal() {
    const modalHTML = `
      <div class="bt-assessment-modal" id="bt-assessment-modal" role="dialog" aria-labelledby="bt-modal-title" aria-modal="true">
        <div class="bt-assessment-modal-content">
          <!-- Header -->
          <div class="bt-modal-header">
            <h2 class="bt-modal-title" id="bt-modal-title">
              <span class="bt-icon">🎯</span>
              <span id="bt-header-text">Find Perfect Toys</span>
            </h2>
            <button class="bt-modal-close" aria-label="Close assessment" type="button">
              <span aria-hidden="true">×</span>
            </button>
          </div>
          
          <!-- Progress Dots -->
          <div class="bt-progress-dots-container" id="bt-progress-dots" style="display: none;">
            <div class="bt-progress-dots-wrapper" id="bt-progress-dots-wrapper">
              <!-- Dots will be dynamically added -->
            </div>
            <div class="bt-progress-info">
              <span id="bt-progress-text">Q1/10</span>
            </div>
            <button class="bt-quick-history-btn" id="bt-quick-history" style="display: none;">
              History
            </button>
          </div>
          
          <!-- Cards Container -->
          <div class="bt-cards-container" id="bt-cards-container">
            <!-- Welcome card shown initially -->
            <div class="bt-welcome-screen" id="bt-welcome-screen">
              <div class="bt-welcome-content">
                <div class="bt-welcome-icon">🎯</div>
                <h3>Find the Perfect Toys for Your Baby</h3>
                <p>This quick assessment will:</p>
                <ul class="bt-welcome-features">
                  <li><span class="bt-check">✓</span> Take only 3-5 minutes</li>
                  <li><span class="bt-check">✓</span> Ask about your baby's development</li>
                  <li><span class="bt-check">✓</span> Recommend 4 personalized toys</li>
                  <li><span class="bt-check">✓</span> Create a free PDF guide</li>
                </ul>
                <button class="bt-primary-button" id="bt-start-button">Start Assessment</button>
              </div>
            </div>
            
            <!-- Initial form card (hidden initially) -->
            <div class="bt-form-card" id="bt-form-card" style="display: none;">
              <div class="bt-card-content">
                <h4>Let's get started! Tell me about your baby:</h4>
                <form id="bt-child-form">
                  <div class="bt-form-group">
                    <label for="bt-child-name">Baby's name:</label>
                    <input type="text" id="bt-child-name" placeholder="e.g., Emma" required>
                  </div>
                  
                  <div class="bt-form-group">
                    <label for="bt-child-dob">Date of birth:</label>
                    <input type="date" id="bt-child-dob" required>
                  </div>
                  
                  <div class="bt-form-group">
                    <label>Was your baby born early? (Optional)</label>
                    <div class="bt-radio-group">
                      <label class="bt-radio-label">
                        <input type="radio" name="bt-preterm" value="no" checked>
                        <span>No, full term (37+ weeks)</span>
                      </label>
                      <label class="bt-radio-label">
                        <input type="radio" name="bt-preterm" value="yes">
                        <span>Yes, early birth</span>
                      </label>
                    </div>
                  </div>
                  
                  <div id="bt-preterm-details" class="bt-preterm-details" style="display: none;">
                    <div class="bt-form-group">
                      <label for="bt-gestational-weeks">Gestational age at birth (weeks):</label>
                      <input type="number" id="bt-gestational-weeks" min="20" max="42" placeholder="e.g., 32">
                      <small>If you know</small>
                    </div>
                  </div>
                  
                  <button type="submit" class="bt-primary-button">Begin Questions →</button>
                </form>
              </div>
            </div>
            
            <!-- Question cards will be inserted here dynamically -->
          </div>
          
          <!-- Navigation Controls -->
          <div class="bt-navigation-controls" id="bt-navigation-controls" style="display: none;">
            <button class="bt-nav-button bt-prev-button" id="bt-prev-button" aria-label="Previous question" disabled>
              <span>←</span>
            </button>
            <button class="bt-nav-button bt-next-button" id="bt-next-button" aria-label="Next question" disabled>
              <span>→</span>
            </button>
          </div>
          
          <!-- Answer History Drawer -->
          <div class="bt-answer-drawer" id="bt-answer-drawer">
            <div class="bt-drawer-handle"></div>
            <div class="bt-drawer-content">
              <div class="bt-drawer-header">
                <h3>Your Answers</h3>
                <button class="bt-drawer-close" id="bt-drawer-close" aria-label="Close drawer">×</button>
              </div>
              <div class="bt-drawer-body" id="bt-drawer-body">
                <!-- Answer list will be populated here -->
              </div>
            </div>
          </div>
          
          <!-- Loading Overlay -->
          <div class="bt-loading-overlay" id="bt-loading-overlay" style="display: none;">
            <div class="bt-loading-content">
              <div class="bt-loading-spinner"></div>
              <p id="bt-loading-text">Preparing questions<span class="bt-loading-dots"></span></p>
              <p class="bt-loading-subtext" id="bt-loading-subtext">This will just take a moment</p>
            </div>
          </div>
        </div>
      </div>
    `;

    document.body.insertAdjacentHTML('beforeend', modalHTML);
    this.modal = document.getElementById('bt-assessment-modal');
    this.cardsContainer = document.getElementById('bt-cards-container');
  }

  /**
   * Bind all event listeners
   */
  bindEvents() {
    // Modal close
    this.modal.querySelector('.bt-modal-close').addEventListener('click', () => this.closeModal());
    
    // ESC key to close
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.modal.classList.contains('active')) {
        if (this.drawerOpen) {
          this.closeDrawer();
        } else {
          this.closeModal();
        }
      }
      
      // Arrow key navigation
      if (this.modal.classList.contains('active') && this.sessionStarted) {
        if (e.key === 'ArrowLeft') {
          this.prevQuestion();
        } else if (e.key === 'ArrowRight') {
          this.nextQuestion();
        }
      }
    });

    // Click outside to close
    this.modal.addEventListener('click', (e) => {
      if (e.target === this.modal) {
        this.closeModal();
      }
    });

    // Start button
    document.getElementById('bt-start-button').addEventListener('click', () => this.showInitialForm());

    // Form submission
    const form = document.getElementById('bt-child-form');
    form.addEventListener('submit', (e) => {
      e.preventDefault();
      this.handleFormSubmission();
    });

    // Preterm radio toggle
    const pretermRadios = document.querySelectorAll('input[name="bt-preterm"]');
    pretermRadios.forEach(radio => {
      radio.addEventListener('change', (e) => {
        document.getElementById('bt-preterm-details').style.display = 
          e.target.value === 'yes' ? 'block' : 'none';
      });
    });

    // Navigation buttons
    document.getElementById('bt-prev-button').addEventListener('click', () => this.prevQuestion());
    document.getElementById('bt-next-button').addEventListener('click', () => this.nextQuestion());
    
    // Answer drawer
    document.getElementById('bt-quick-history').addEventListener('click', () => this.toggleDrawer());
    document.getElementById('bt-drawer-close').addEventListener('click', () => this.closeDrawer());
    
    // Touch gestures
    this.bindTouchGestures();
  }

  /**
   * Bind touch gesture events for swipe navigation
   */
  bindTouchGestures() {
    let startX, startY, startTime;

    this.cardsContainer.addEventListener('touchstart', (e) => {
      if (!this.sessionStarted) return;
      
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
      startTime = Date.now();
      this.isSwiping = false;
    }, { passive: true });

    this.cardsContainer.addEventListener('touchmove', (e) => {
      if (!this.sessionStarted) return;
      
      const currentX = e.touches[0].clientX;
      const currentY = e.touches[0].clientY;
      const diffX = Math.abs(currentX - startX);
      const diffY = Math.abs(currentY - startY);
      
      // Determine if this is a horizontal swipe
      if (diffX > diffY && diffX > 10) {
        this.isSwiping = true;
      }
    }, { passive: true });

    this.cardsContainer.addEventListener('touchend', (e) => {
      if (!this.sessionStarted || !this.isSwiping) return;
      
      const endX = e.changedTouches[0].clientX;
      const endY = e.changedTouches[0].clientY;
      const diffX = endX - startX;
      const diffY = Math.abs(endY - startY);
      const diffTime = Date.now() - startTime;
      const velocity = Math.abs(diffX) / diffTime;
      
      // Horizontal swipe (left/right)
      if (Math.abs(diffX) > diffY) {
        // Swipe left (next question)
        if (diffX < -50 || velocity > 0.5) {
          this.nextQuestion();
        }
        // Swipe right (previous question)
        else if (diffX > 50 || velocity > 0.5) {
          this.prevQuestion();
        }
      }
      
      this.isSwiping = false;
    }, { passive: true });
  }

  /**
   * Open the assessment modal
   */
  openModal() {
    this.modal.classList.add('active');
    document.body.style.overflow = 'hidden';
    
    // Reset to welcome screen if not started
    if (!this.sessionStarted) {
      document.getElementById('bt-welcome-screen').style.display = 'flex';
      document.getElementById('bt-form-card').style.display = 'none';
    }
  }

  /**
   * Close the assessment modal
   */
  closeModal() {
    // Confirm if assessment is in progress
    if (this.sessionStarted && !this.assessmentComplete) {
      if (!confirm('Are you sure you want to exit? Your progress will be lost.')) {
        return;
      }
    }
    
    this.modal.classList.remove('active');
    document.body.style.overflow = '';
  }

  /**
   * Show initial form
   */
  showInitialForm() {
    document.getElementById('bt-welcome-screen').style.display = 'none';
    document.getElementById('bt-form-card').style.display = 'flex';
  }

  /**
   * Handle form submission and start session
   */
  async handleFormSubmission() {
    const childName = document.getElementById('bt-child-name').value.trim();
    const dob = document.getElementById('bt-child-dob').value;
    const isPreterm = document.querySelector('input[name="bt-preterm"]:checked').value === 'yes';
    const gestationalWeeks = isPreterm ? parseInt(document.getElementById('bt-gestational-weeks').value) || 40 : 40;

    if (!childName || !dob) {
      alert('Please fill in all required fields.');
      return;
    }

    try {
      this.showLoading('Setting up your assessment', 'Creating personalized questions for ' + childName);
      
      const sessionData = await this.api.startSession(childName, dob, gestationalWeeks, null);
      
      this.hideLoading();
      this.sessionStarted = true;
      
      // Hide form, show navigation
      document.getElementById('bt-form-card').style.display = 'none';
      document.getElementById('bt-navigation-controls').style.display = 'flex';
      document.getElementById('bt-progress-dots').style.display = 'flex';
      document.getElementById('bt-quick-history').style.display = 'inline-block';
      document.getElementById('bt-header-text').textContent = `${sessionData.child_name}'s Assessment`;
      
      // Start first question
      await this.fetchNextQuestion();
      
    } catch (error) {
      this.hideLoading();
      alert(`Failed to start assessment: ${error.message}`);
      console.error('Session start error:', error);
    }
  }

  /**
   * Fetch next question from API
   */
  async fetchNextQuestion(answerCode = null, elapsedMs = null) {
    try {
      const loadingMessages = [
        { text: 'Analyzing response', subtext: 'Understanding your answer' },
        { text: 'Compiling next question', subtext: 'Tailoring to your baby\'s development' },
        { text: 'Preparing question', subtext: 'Just a moment' },
        { text: 'Loading next question', subtext: 'Almost ready' }
      ];
      const randomMsg = loadingMessages[Math.floor(Math.random() * loadingMessages.length)];
      this.showLoading(randomMsg.text, randomMsg.subtext);
      
      const userMessage = answerCode ? this.formatAnswerOption(answerCode) : 'Ready to begin assessment';
      const response = await this.api.queryAssistant(userMessage, answerCode, 'sure', elapsedMs);
      
      this.hideLoading();
      
      // Handle both single message and array of messages
      const messages = Array.isArray(response) ? response : [response];
      
      // Separate feedback and question
      let feedbackData = null;
      let questionData = null;
      
      for (const msg of messages) {
        if (msg.answer_options && msg.answer_options.length > 0) {
          questionData = msg;
        } else if (msg.content || msg.question) {
          feedbackData = msg;
        }
      }
      
      // Check if assessment is complete
      // Complete if: explicit session_complete flag OR no more questions returned
      const shouldComplete = !questionData || (questionData && questionData.session_complete);
      
      if (shouldComplete) {
        // Show final feedback if any
        if (feedbackData) {
          this.showFeedbackCard(feedbackData);
          await this.delay(2500);
        }
        
        await this.completeAssessment();
        return;
      }
      
      // Add to question history
      if (questionData) {
        this.questionHistory.push({
          index: this.questionHistory.length,
          questionData: questionData,
          feedbackData: feedbackData,
          selectedAnswer: null,
          timestamp: Date.now()
        });
        
        this.currentQuestionIndex = this.questionHistory.length - 1;
        this.createQuestionCard(this.currentQuestionIndex);
        this.updateProgressDots();
        this.updateNavigationButtons();
        
        // Show feedback if exists, then auto-advance to question
        if (feedbackData) {
          this.showFeedbackCard(feedbackData);
          await this.delay(2500);
        }
        
        this.goToQuestion(this.currentQuestionIndex);
      }
      
    } catch (error) {
      this.hideLoading();
      alert(`Failed to get question: ${error.message}`);
      console.error('Question fetch error:', error);
    }
  }

  /**
   * Create a question card in the DOM
   */
  createQuestionCard(index) {
    const item = this.questionHistory[index];
    const question = item.questionData;
    
    const cardHTML = `
      <div class="bt-question-card" id="bt-card-${index}" data-index="${index}" style="display: none;">
        <div class="bt-card-content">
          <div class="bt-domain-badge">${question.domain}</div>
          <div class="bt-question-text">${question.question}</div>
          ${question.clarifier ? `<div class="bt-question-tip">💡 ${question.clarifier}</div>` : ''}
          
          <div class="bt-answer-options" id="bt-options-${index}">
            ${question.answer_options.map(option => `
              <button class="bt-answer-option" data-answer="${option}" data-index="${index}" type="button">
                ${this.formatAnswerOption(option)}
              </button>
            `).join('')}
          </div>
          
          ${item.selectedAnswer ? `<div class="bt-selected-indicator">✓ Answered: ${this.formatAnswerOption(item.selectedAnswer)}</div>` : ''}
        </div>
      </div>
    `;
    
    this.cardsContainer.insertAdjacentHTML('beforeend', cardHTML);
    
    // Bind answer button events
    const answerButtons = document.querySelectorAll(`#bt-options-${index} .bt-answer-option`);
    answerButtons.forEach(btn => {
      btn.addEventListener('click', (e) => {
        const answer = e.target.dataset.answer;
        const questionIndex = parseInt(e.target.dataset.index);
        this.handleAnswer(questionIndex, answer);
      });
    });
  }

  /**
   * Show feedback card temporarily
   */
  showFeedbackCard(feedback) {
    const feedbackHTML = `
      <div class="bt-feedback-card" id="bt-feedback-temp">
        <div class="bt-card-content">
          <div class="bt-feedback-icon">👍</div>
          <div class="bt-feedback-text">${feedback.content || feedback.question}</div>
        </div>
      </div>
    `;
    
    this.cardsContainer.insertAdjacentHTML('beforeend', feedbackHTML);
    document.getElementById('bt-feedback-temp').style.display = 'flex';
    
    // Auto-remove after display
    setTimeout(() => {
      const temp = document.getElementById('bt-feedback-temp');
      if (temp) temp.remove();
    }, 3000);
  }

  /**
   * Handle answer selection
   */
  async handleAnswer(questionIndex, answer) {
    const item = this.questionHistory[questionIndex];
    
    // If already answered and we're reviewing, don't allow changes
    if (item.selectedAnswer !== null && questionIndex !== this.currentQuestionIndex) {
      return;
    }
    
    // Mark answer as selected
    item.selectedAnswer = answer;
    item.timestamp = Date.now();
    
    // Update UI to show selected answer
    const card = document.getElementById(`bt-card-${questionIndex}`);
    const options = card.querySelectorAll('.bt-answer-option');
    options.forEach(opt => {
      opt.classList.remove('selected');
      if (opt.dataset.answer === answer) {
        opt.classList.add('selected');
      }
    });
    
    // Update selected indicator
    let indicator = card.querySelector('.bt-selected-indicator');
    if (!indicator) {
      indicator = document.createElement('div');
      indicator.className = 'bt-selected-indicator';
      card.querySelector('.bt-card-content').appendChild(indicator);
    }
    indicator.textContent = `✓ Answered: ${this.formatAnswerOption(answer)}`;
    
    this.updateProgressDots();
    this.updateNavigationButtons();
    this.updateDrawerContent();
    
    // Calculate elapsed time
    const elapsedMs = Date.now() - item.timestamp;
    
    // Always fetch next question - API will tell us when to complete
    await this.delay(500);
    await this.fetchNextQuestion(answer, elapsedMs);
  }

  /**
   * Navigate to a specific question
   */
  goToQuestion(index) {
    if (index < 0 || index >= this.questionHistory.length) return;
    
    // Hide all cards
    const allCards = this.cardsContainer.querySelectorAll('.bt-question-card');
    allCards.forEach(card => {
      card.style.display = 'none';
      card.classList.remove('active');
    });
    
    // Show target card with animation
    const targetCard = document.getElementById(`bt-card-${index}`);
    if (targetCard) {
      targetCard.style.display = 'flex';
      setTimeout(() => targetCard.classList.add('active'), 10);
    }
    
    this.currentQuestionIndex = index;
    this.updateNavigationButtons();
    this.updateProgressDots();
  }

  /**
   * Previous question
   */
  prevQuestion() {
    if (this.currentQuestionIndex > 0) {
      this.goToQuestion(this.currentQuestionIndex - 1);
    }
  }

  /**
   * Next question
   */
  nextQuestion() {
    const currentItem = this.questionHistory[this.currentQuestionIndex];
    
    // Only allow next if current question is answered OR we're reviewing
    if (currentItem && currentItem.selectedAnswer !== null) {
      if (this.currentQuestionIndex < this.questionHistory.length - 1) {
        this.goToQuestion(this.currentQuestionIndex + 1);
      }
    }
  }

  /**
   * Update progress dots
   */
  updateProgressDots() {
    const wrapper = document.getElementById('bt-progress-dots-wrapper');
    const progressText = document.getElementById('bt-progress-text');
    const totalQuestions = Math.max(this.questionHistory.length, this.minQuestions);
    
    let dotsHTML = '';
    for (let i = 0; i < totalQuestions; i++) {
      const item = this.questionHistory[i];
      let className = 'bt-progress-dot';
      
      if (item) {
        if (item.selectedAnswer !== null) {
          className += ' completed';
        }
        if (i === this.currentQuestionIndex) {
          className += ' current';
        }
      }
      
      dotsHTML += `<div class="${className}" data-index="${i}"></div>`;
    }
    
    wrapper.innerHTML = dotsHTML;
    
    // Update progress text
    const currentNum = this.currentQuestionIndex + 1;
    const totalNum = totalQuestions;
    progressText.textContent = `Q${currentNum}/${totalNum}`;
    
    // Add click events to dots
    wrapper.querySelectorAll('.bt-progress-dot').forEach(dot => {
      dot.addEventListener('click', () => {
        const index = parseInt(dot.dataset.index);
        if (index < this.questionHistory.length) {
          this.goToQuestion(index);
        }
      });
    });
  }

  /**
   * Update navigation button states
   */
  updateNavigationButtons() {
    const prevBtn = document.getElementById('bt-prev-button');
    const nextBtn = document.getElementById('bt-next-button');
    
    // Previous button
    prevBtn.disabled = this.currentQuestionIndex === 0;
    
    // Next button
    const currentItem = this.questionHistory[this.currentQuestionIndex];
    const isAnswered = currentItem && currentItem.selectedAnswer !== null;
    const hasNext = this.currentQuestionIndex < this.questionHistory.length - 1;
    
    nextBtn.disabled = !isAnswered || !hasNext;
  }


  /**
   * Toggle answer drawer
   */
  toggleDrawer() {
    if (this.drawerOpen) {
      this.closeDrawer();
    } else {
      this.openDrawer();
    }
  }

  /**
   * Open answer history drawer
   */
  openDrawer() {
    this.updateDrawerContent();
    document.getElementById('bt-answer-drawer').classList.add('open');
    this.drawerOpen = true;
  }

  /**
   * Close answer history drawer
   */
  closeDrawer() {
    document.getElementById('bt-answer-drawer').classList.remove('open');
    this.drawerOpen = false;
  }

  /**
   * Update drawer content with answer history
   */
  updateDrawerContent() {
    const drawerBody = document.getElementById('bt-drawer-body');
    
    if (this.questionHistory.length === 0) {
      drawerBody.innerHTML = '<p class="bt-drawer-empty">No questions yet</p>';
      return;
    }
    
    let contentHTML = '<div class="bt-answer-list">';
    
    this.questionHistory.forEach((item, index) => {
      const isAnswered = item.selectedAnswer !== null;
      const isCurrent = index === this.currentQuestionIndex;
      
      contentHTML += `
        <div class="bt-answer-item ${isCurrent ? 'current' : ''}" data-index="${index}">
          <div class="bt-answer-item-header">
            <span class="bt-answer-number">Q${index + 1}</span>
            <span class="bt-answer-domain">${item.questionData.domain}</span>
            ${isAnswered ? '<span class="bt-answer-status">✓</span>' : '<span class="bt-answer-status pending">○</span>'}
          </div>
          <div class="bt-answer-item-question">${item.questionData.question}</div>
          ${isAnswered ? `<div class="bt-answer-item-answer">Answer: ${this.formatAnswerOption(item.selectedAnswer)}</div>` : '<div class="bt-answer-item-answer pending">Not answered yet</div>'}
        </div>
      `;
    });
    
    contentHTML += '</div>';
    drawerBody.innerHTML = contentHTML;
    
    // Add click events to navigate to questions
    drawerBody.querySelectorAll('.bt-answer-item').forEach(item => {
      item.addEventListener('click', () => {
        const index = parseInt(item.dataset.index);
        this.goToQuestion(index);
        this.closeDrawer();
      });
    });
  }

  /**
   * Complete the assessment
   */
  async completeAssessment() {
    try {
      this.showLoading('Analyzing responses', 'Creating your personalized report');
      
      // Close the session
      await this.api.closeSession();
      
      this.hideLoading();
      this.assessmentComplete = true;
      
      // Show completion message
      this.showCompletionMessage();
      
    } catch (error) {
      this.hideLoading();
      alert('Failed to complete assessment. Please try again.');
      console.error('Completion error:', error);
    }
  }

  /**
   * Show completion message
   */
  showCompletionMessage() {
    const completionHTML = `
      <div class="bt-completion-card" style="display: flex;">
        <div class="bt-card-content">
          <div class="bt-completion-icon">🎉</div>
          <h3>Assessment Complete!</h3>
          <p>Your personalized report is ready</p>
          <button class="bt-primary-button" onclick="window.location.href='/pages/assessment-results?session_id=${this.api.getSessionId()}'">
            View My Results →
          </button>
        </div>
      </div>
    `;
    
    // Hide all question cards and navigation
    document.querySelectorAll('.bt-question-card').forEach(card => card.style.display = 'none');
    document.getElementById('bt-navigation-controls').style.display = 'none';
    
    this.cardsContainer.insertAdjacentHTML('beforeend', completionHTML);
    
    // Auto-navigate after 5 seconds
    setTimeout(() => {
      window.location.href = `/pages/assessment-results?session_id=${this.api.getSessionId()}`;
    }, 5000);
  }

  /**
   * Format answer option text
   */
  formatAnswerOption(option) {
    const options = {
      'yes': 'Yes',
      'sometimes': 'Sometimes',
      'no': 'No',
      'not_sure': 'Not Sure'
    };
    return options[option] || option;
  }

  /**
   * Show loading overlay with engaging messages
   */
  showLoading(message, subtext = 'Please wait') {
    const overlay = document.getElementById('bt-loading-overlay');
    const textEl = document.getElementById('bt-loading-text');
    const subtextEl = document.getElementById('bt-loading-subtext');
    
    // Add animated dots
    textEl.innerHTML = `${message}<span class="bt-loading-dots"></span>`;
    subtextEl.textContent = subtext;
    overlay.style.display = 'flex';
  }

  /**
   * Hide loading overlay
   */
  hideLoading() {
    document.getElementById('bt-loading-overlay').style.display = 'none';
  }

  /**
   * Utility: Delay
   */
  delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  window.BrainyTotsAssessment = new BrainyTotsAssessment();
});
